import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Mail } from "lucide-react";

interface Channel {
  id: string;
  name: string;
  enabled: boolean;
  requiresVerification?: boolean;
  isVerified?: boolean;
}

interface ChannelPreferencesProps {
  leadId: string;
}

export const ChannelPreferences = ({ leadId }: ChannelPreferencesProps) => {
  const { toast } = useToast();
  const [channels, setChannels] = useState<Channel[]>([
    { 
      id: "email", 
      name: "Email", 
      enabled: true, 
      requiresVerification: true,
      isVerified: false 
    },
    { id: "sms", name: "SMS", enabled: false },
    { id: "push", name: "Push Notifications", enabled: false },
  ]);

  useEffect(() => {
    loadPreferences();
  }, [leadId]);

  const loadPreferences = async () => {
    try {
      // Load channel preferences
      const { data: preferences, error: prefError } = await supabase
        .from("channel_preferences")
        .select("*")
        .eq("lead_id", leadId);

      if (prefError) throw prefError;

      // Load lead verification status
      const { data: lead, error: leadError } = await supabase
        .from("newsletter_leads")
        .select("email_verified_at")
        .eq("id", leadId)
        .single();

      if (leadError) throw leadError;

      const updatedChannels = channels.map(channel => ({
        ...channel,
        enabled: preferences?.some(pref => 
          pref.channel === channel.id && pref.is_enabled
        ) ?? channel.enabled,
        isVerified: channel.id === "email" ? !!lead?.email_verified_at : undefined
      }));

      setChannels(updatedChannels);
    } catch (error) {
      console.error("Error loading preferences:", error);
      toast({
        title: "Error",
        description: "Failed to load preferences",
        variant: "destructive",
      });
    }
  };

  const handleToggle = async (channelId: string) => {
    try {
      const channel = channels.find(c => c.id === channelId);
      if (!channel) return;

      if (channel.requiresVerification && !channel.isVerified) {
        await sendVerificationEmail();
        return;
      }

      const updatedChannels = channels.map(channel =>
        channel.id === channelId
          ? { ...channel, enabled: !channel.enabled }
          : channel
      );
      setChannels(updatedChannels);

      const { error } = await supabase
        .from("channel_preferences")
        .upsert({
          lead_id: leadId,
          channel: channelId,
          is_enabled: updatedChannels.find(c => c.id === channelId)?.enabled,
        });

      if (error) throw error;

      toast({
        title: "Success",
        description: "Channel preferences have been saved.",
      });
    } catch (error) {
      console.error("Error updating preferences:", error);
      toast({
        title: "Error",
        description: "Failed to update preferences",
        variant: "destructive",
      });
    }
  };

  const sendVerificationEmail = async () => {
    try {
      const { error } = await supabase.functions.invoke("email-verification", {
        body: { action: "SEND_VERIFICATION", leadId }
      });

      if (error) throw error;

      toast({
        title: "Verification Email Sent",
        description: "Please check your inbox and verify your email address.",
      });
    } catch (error) {
      console.error("Error sending verification email:", error);
      toast({
        title: "Error",
        description: "Failed to send verification email",
        variant: "destructive",
      });
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Communication Channels</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {channels.map((channel) => (
            <div
              key={channel.id}
              className="flex items-center justify-between"
            >
              <div className="flex items-center gap-2">
                <Label htmlFor={channel.id}>{channel.name}</Label>
                {channel.requiresVerification && !channel.isVerified && (
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => sendVerificationEmail()}
                  >
                    <Mail className="h-4 w-4 mr-2" />
                    Verify Email
                  </Button>
                )}
              </div>
              <Switch
                id={channel.id}
                checked={channel.enabled}
                onCheckedChange={() => handleToggle(channel.id)}
                disabled={channel.requiresVerification && !channel.isVerified}
              />
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};